import Vue, { CreateElement } from 'vue'
import { Component, Prop } from 'vue-property-decorator'

import { Subtitle1 } from '@src/elements/typography/typography'
import { Button } from '@src/elements/button/button'
import { Select } from '@src/elements/select/select'
import { Option } from '@src/elements/select/option'
import { Input } from '@src/elements/input/input'
import Member, { ExpertiseLevel, FeedbackVerbosity } from '@src/data/member'
import { ContentBox } from '@src/elements/content-box/content-box'
import { MemberService } from '@src/service/member'
import { MemberProfile } from '@src/data/member-profile'
import SessionManager from '@src/lib/session_manager'
// import { OptionGroup } from '@src/elements/select/option-group'

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember || this.$store.state.memberProfile
    },
    clubOnlyFieldsReadonly () {
      return !SessionManager.Instance.isManager()
    }
  }
})
export default class MemberSettings extends Vue {

  @Prop({ required: false, default: false })
  readonly: boolean

  protected member: Member | MemberProfile
  protected service: MemberService = new MemberService()
  protected clubOnlyFieldsReadonly: boolean

  constructor () {
    super()
  }

  update () {
    const settings = this.clubOnlyFieldsReadonly ? {
      emailNotification: this.member.emailNotifications,
      hiddenFromLeaderboard: this.member.hiddenFromLeaderboard
    } : {
      expertiseLevel: this.member.expertiseLevel,
      feedbackVerbosity: this.member.feedbackVerbosity,
      displayPixScore: this.member.displayPixScore,
      emailNotification: this.member.emailNotifications,
      hiddenFromLeaderboard: this.member.hiddenFromLeaderboard
    }

    return this.service.updateSettings(this.member.id, settings)
      .then(() => {
        this.$store.dispatch('showToast', { message: this.$t('PAGE.MEMBERS.PERSONAL_SETTINGS_SAVED'), fadeOut: true })
      })
  }

  render (h: CreateElement) {
    return <div class='personal-settings' direction='column'>
      <Subtitle1>{this.$t('PAGE.MEMBERS.PERSONAL_SETTINGS')}</Subtitle1>

      <ContentBox>
        <div slot='content'>
          <div class='row'>
            {this.clubOnlyFieldsReadonly ? (
              <Input type='text'
                     value={this.$t(`PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.${this.member.expertiseLevel.toUpperCase()}`)}
                     label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.DEFAULT_EXERCISES_DIFFICULTY')}
                     class='short'
                     readonly={true} />
            ) : (
              <Select ref='level-key' value={this.$t(`PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.${this.member.expertiseLevel.toUpperCase()}`)}
                      valueChanged={(value: ExpertiseLevel) => this.member.expertiseLevel = value}
                      label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.DEFAULT_EXERCISES_DIFFICULTY')} class='short'
                      readonly={this.readonly} >
                <Option type='single-option' value={ExpertiseLevel.BEGINNER} label={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.BEGINNER')} selected={this.member.expertiseLevel === ExpertiseLevel.BEGINNER} />
                <Option type='single-option' value={ExpertiseLevel.INTERMEDIATE} label={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.INTERMEDIATE')}
                        selected={this.member.expertiseLevel === ExpertiseLevel.INTERMEDIATE} />
                <Option type='single-option' value={ExpertiseLevel.ADVANCED} label={this.$t('PAGE.MEMBERS.STATUS.EXPERTISE_LEVEL.ADVANCED')} selected={this.member.expertiseLevel === ExpertiseLevel.ADVANCED} />
              </Select>
            )}

            {this.clubOnlyFieldsReadonly ? (
              <Input type='text'
                     value={this.member.feedbackVerbosity ? this.$t(`PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_${this.member.feedbackVerbosity.toUpperCase()}`) : ''}
                     label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.FEEDBACK_DURING_EXERCISE')}
                     class='short'
                     readonly={true} />
            ) : (
              <Select ref='verbosity' value={this.member.feedbackVerbosity ? this.$t(`PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_${this.member.feedbackVerbosity.toUpperCase()}`) : ''}
                      valueChanged={(value: FeedbackVerbosity) => this.member.feedbackVerbosity = value} class='short'
                      label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.FEEDBACK_DURING_EXERCISE')}
                      readonly={this.readonly} >
                <Option type='single-option' value='full' label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_FULL')} selected={this.member.feedbackVerbosity === 'full'} />
                <Option type='single-option' value='dots_only' label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_DOTS_ONLY')}
                        selected={this.member.feedbackVerbosity === 'dots_only'} />
                <Option type='single-option' value='none' label={this.$t('PAGE.CLUB.ARRANGEMENT_SETTINGS.SHOW_NONE')} selected={this.member.feedbackVerbosity === 'none'} />
              </Select>
            )}
            {this.clubOnlyFieldsReadonly ? (
              <Input type='text'
                     value={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.${this.member.displayPixScore ? 'SHOW' : 'HIDE'}`)}
                     label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.OPTION')}
                     class='short'
                     readonly={true} />
            ) : (
              <Select label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.OPTION')} class='short'
                      valueChanged={(value) => this.member.displayPixScore = value === 'show'} readonly={this.readonly}
                      value={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.${this.member.displayPixScore ? 'SHOW' : 'HIDE'}`)}>
                <Option type='single-option' value='show' label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.SHOW`)} selected={!this.member.displayPixScore} />
                <Option type='single-option' value='hide' label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.HIDE`)} selected={this.member.displayPixScore} />
              </Select>
            )}
            <Select label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.LEADERBOARD.OPTION')} class='short'
                    valueChanged={(value) => this.member.hiddenFromLeaderboard = value !== 'show'} readonly={this.readonly}
                    value={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.DISPLAY_PIX_SCORE.${!this.member.hiddenFromLeaderboard ? 'SHOW' : 'HIDE'}`)}>
              <Option type='single-option' value='show' label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.LEADERBOARD.SHOW`)} selected={!this.member.hiddenFromLeaderboard} />
              <Option type='single-option' value='hide' label={this.$t(`PAGE.CLUB.GENERAL_SETTINGS.LEADERBOARD.HIDE`)} selected={this.member.hiddenFromLeaderboard} />
            </Select>
            {/*<div class='short'>*/}
            {/*  <Select*/}
            {/*    placeholder={this.$t('PAGE.CLUB.GENERAL_SETTINGS.EMAIL_NOTIFICATION')}*/}
            {/*    class='short'*/}
            {/*    value={this.$t('PAGE.CLUB.GENERAL_SETTINGS.EMAIL_NOTIFICATION')}*/}
            {/*    multiple*/}
            {/*  >*/}
            {/*    <OptionGroup>*/}
            {/*      <Option value={this.$t('PAGE.CLUB.GENERAL_SETTINGS.TRAINING_NOTIFICATION') as string}*/}
            {/*              label={this.$t('PAGE.CLUB.GENERAL_SETTINGS.TRAINING_NOTIFICATION') as string}*/}
            {/*              select={(value) => this.setValue(value)}*/}
            {/*              selected={this.member.emailNotifications.includes(this.$t('PAGE.CLUB.GENERAL_SETTINGS.TRAINING_NOTIFICATION') as string)}/>*/}
            {/*    </OptionGroup>*/}
            {/*  </Select>*/}
            {/*</div>*/}

          </div>
        </div>

        {!this.readonly &&
          <div class='btn-row' slot='bottom'>
            <Button class='update-btn' label={this.$t('UPDATE')} click={this.update} ref='updateBtn' type='elevated'/>
          </div>
        }
      </ContentBox>
    </div>
  }

  // setValue (value: string) {
  //   if (!this.member.emailNotifications) {
  //     this.member.emailNotifications = []
  //   }
  //   if (this.member.emailNotifications.includes(value)) {
  //     this.member.emailNotifications = this.member.emailNotifications.filter(item => item !== value)
  //   } else {
  //     this.member.emailNotifications.push(value)
  //   }
  // }
}
